import React, { useState, useEffect, useRef } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import CircularProgress from "@material-ui/core/CircularProgress";
import {
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Typography,
  Tabs,
  Tab,
  Box,
  Switch,
  FormControlLabel,
  Slider,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
} from "@material-ui/core";
import { Visibility, VisibilityOff, Send as SendIcon, PlayArrow, Stop, Add, Delete, CloudUpload, Link, Description, TextFields } from "@material-ui/icons";
import { SmartToy, Audiotrack as AudioFile, Videocam as VideoFile } from "@mui/icons-material";
import QueueSelectSingle from "../../components/QueueSelectSingle";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  root: { display: "flex", flexWrap: "wrap" },
  dialogHeader: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(2, 3),
    gap: theme.spacing(1.5),
    borderBottom: "none",
  },
  headerIcon: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    background: "linear-gradient(135deg, #F97316, #FB923C)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontSize: 20,
  },
  tabs: {
    minHeight: 36,
    "& .MuiTab-root": {
      minHeight: 36,
      textTransform: "none",
      fontWeight: 500,
      fontSize: 14,
      color: theme.palette.text.secondary,
    },
    "& .Mui-selected": {
      color: "#F97316",
    },
    "& .MuiTabs-indicator": {
      backgroundColor: "#F97316",
    },
  },
  sectionContent: {
    padding: theme.spacing(2, 3),
    overflowY: "auto",
    maxHeight: "60vh",
  },
  fieldRow: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  label: {
    fontWeight: 600,
    fontSize: 14,
    marginBottom: 4,
    marginTop: theme.spacing(1.5),
    color: theme.palette.text.primary,
  },
  helperText: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  languageChips: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 8,
  },
  chipActive: {
    backgroundColor: "#F97316 !important",
    color: "#fff !important",
    fontWeight: 500,
  },
  chipInactive: {
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.primary,
    cursor: "pointer",
  },
  switchRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: theme.spacing(1.5),
  },
  sliderBox: {
    marginTop: theme.spacing(1),
    "& .MuiSlider-root": {
      color: "#F97316",
    },
  },
  audioSection: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
  },
  actions: {
    padding: theme.spacing(2, 3),
    gap: theme.spacing(1),
  },
  saveBtn: {
    backgroundColor: "#F97316",
    color: "#fff",
    "&:hover": { backgroundColor: "#EA580C" },
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 8,
    padding: "8px 32px",
  },
  cancelBtn: {
    textTransform: "none",
    borderRadius: 8,
    padding: "8px 24px",
  },
  btnWrapper: { position: "relative" },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  // Test chat styles
  testChatContainer: {
    display: "flex",
    flexDirection: "column",
    height: 400,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    overflow: "hidden",
    marginTop: theme.spacing(1),
  },
  messagesArea: {
    flex: 1,
    overflowY: "auto",
    padding: theme.spacing(2),
    backgroundColor: theme.palette.type === "dark" ? "#1a1a2e" : "#f5f5f5",
  },
  messageBubble: {
    maxWidth: "80%",
    padding: theme.spacing(1, 1.5),
    borderRadius: 12,
    marginBottom: theme.spacing(1),
    wordWrap: "break-word",
    whiteSpace: "pre-wrap",
    fontSize: "0.875rem",
  },
  userMessage: {
    backgroundColor: "#F97316",
    color: "#fff",
    marginLeft: "auto",
    borderBottomRightRadius: 4,
  },
  aiMessage: {
    backgroundColor: theme.palette.type === "dark" ? "#2d2d44" : "#fff",
    color: theme.palette.text.primary,
    marginRight: "auto",
    borderBottomLeftRadius: 4,
    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
  },
  inputArea: {
    display: "flex",
    padding: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  modelSelect: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1),
    gap: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  typingIndicator: {
    display: "flex",
    gap: 4,
    padding: theme.spacing(1, 1.5),
    "& span": {
      width: 8,
      height: 8,
      borderRadius: "50%",
      backgroundColor: theme.palette.text.secondary,
      animation: "$bounce 1.4s infinite ease-in-out both",
    },
    "& span:nth-child(1)": { animationDelay: "-0.32s" },
    "& span:nth-child(2)": { animationDelay: "-0.16s" },
  },
  "@keyframes bounce": {
    "0%, 80%, 100%": { transform: "scale(0)" },
    "40%": { transform: "scale(1)" },
  },
}));

const AI_MODELS = [
  { value: "gpt-4o-mini", label: "GPT-4o Mini", descKey: "promptModal.aiModels.gpt4oMiniDesc" },
  { value: "gpt-4o", label: "GPT-4o", descKey: "promptModal.aiModels.gpt4oDesc" },
  { value: "gpt-4.1-mini", label: "GPT-4.1 Mini", descKey: "promptModal.aiModels.gpt41MiniDesc" },
  { value: "gpt-4.1", label: "GPT-4.1", descKey: "promptModal.aiModels.gpt41Desc" },
  { value: "gpt-4.1-nano", label: "GPT-4.1 Nano", descKey: "promptModal.aiModels.gpt41NanoDesc" },
  { value: "o4-mini", label: "O4 Mini", descKey: "promptModal.aiModels.o4MiniDesc" },
];

const VOICE_TONES = [
  { value: "amigavel", labelKey: "promptModal.voiceTones.amigavel" },
  { value: "profissional", labelKey: "promptModal.voiceTones.profissional" },
  { value: "formal", labelKey: "promptModal.voiceTones.formal" },
  { value: "casual", labelKey: "promptModal.voiceTones.casual" },
  { value: "empatico", labelKey: "promptModal.voiceTones.empatico" },
  { value: "tecnico", labelKey: "promptModal.voiceTones.tecnico" },
];

const FUNCTIONS = [
  { value: "geral", labelKey: "promptModal.functions.geral", descKey: "promptModal.functions.geralDesc" },
  { value: "vendas", labelKey: "promptModal.functions.vendas", descKey: "promptModal.functions.vendasDesc" },
  { value: "suporte", labelKey: "promptModal.functions.suporte", descKey: "promptModal.functions.suporteDesc" },
  { value: "agendamento", labelKey: "promptModal.functions.agendamento", descKey: "promptModal.functions.agendamentoDesc" },
  { value: "financeiro", labelKey: "promptModal.functions.financeiro", descKey: "promptModal.functions.financeiroDesc" },
];

const LANGUAGES = [
  { code: "pt-BR", flag: "🇧🇷", label: "BR Português (BR)" },
  { code: "en-US", flag: "🇺🇸", label: "US English" },
  { code: "es-ES", flag: "🇪🇸", label: "ES Español" },
  { code: "fr-FR", flag: "🇫🇷", label: "FR Français" },
  { code: "de-DE", flag: "🇩🇪", label: "DE Deutsch" },
  { code: "it-IT", flag: "🇮🇹", label: "IT Italiano" },
  { code: "auto", flag: "🌐", label: "Auto" },
];

const RESPONSE_MODES = [
  { value: "text", labelKey: "promptModal.responseModes.text", descKey: "promptModal.responseModes.textDesc" },
  { value: "audio", labelKey: "promptModal.responseModes.audio", descKey: "promptModal.responseModes.audioDesc" },
  { value: "both", labelKey: "promptModal.responseModes.both", descKey: "promptModal.responseModes.bothDesc" },
];

const AGENT_VOICES = [
  { value: "alloy", label: "Alloy", descKey: "promptModal.agentVoices.alloyDesc" },
  { value: "ash", label: "Ash", descKey: "promptModal.agentVoices.ashDesc" },
  { value: "ballad", label: "Ballad", descKey: "promptModal.agentVoices.balladDesc" },
  { value: "coral", label: "Coral", descKey: "promptModal.agentVoices.coralDesc" },
  { value: "echo", label: "Echo", descKey: "promptModal.agentVoices.echoDesc" },
  { value: "fable", label: "Fable", descKey: "promptModal.agentVoices.fableDesc" },
  { value: "onyx", label: "Onyx", descKey: "promptModal.agentVoices.onyxDesc" },
  { value: "nova", label: "Nova", descKey: "promptModal.agentVoices.novaDesc" },
  { value: "sage", label: "Sage", descKey: "promptModal.agentVoices.sageDesc" },
  { value: "shimmer", label: "Shimmer", descKey: "promptModal.agentVoices.shimmerDesc" },
];

const PromptModal = ({ open, onClose, promptId }) => {
  const classes = useStyles();
  const t = (key, opts) => i18n.t(key, opts);
  const [activeTab, setActiveTab] = useState(0);
  const [showApiKey, setShowApiKey] = useState(false);

  // Test chat state
  const [testMessages, setTestMessages] = useState([]);
  const [testInput, setTestInput] = useState("");
  const [testLoading, setTestLoading] = useState(false);
  const [testModel, setTestModel] = useState("gpt-4o-mini");
  const [playingVoice, setPlayingVoice] = useState(false);
  const audioRef = useRef(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  // Knowledge base state
  const [knowledgeItems, setKnowledgeItems] = useState([]);
  const [knowledgeLoading, setKnowledgeLoading] = useState(false);
  const [addKnowledgeOpen, setAddKnowledgeOpen] = useState(false);
  const [newKnowledge, setNewKnowledge] = useState({ name: "", type: "text", content: "" });
  const [uploadingFile, setUploadingFile] = useState(false);

  const fetchKnowledgeItems = async () => {
    if (!promptId) return;
    setKnowledgeLoading(true);
    try {
      const { data } = await api.get(`/prompt/${promptId}/knowledge`);
      setKnowledgeItems(data);
    } catch (err) {
      console.error("Erro ao carregar base de conhecimento:", err);
    } finally {
      setKnowledgeLoading(false);
    }
  };

  useEffect(() => {
    if (promptId && open) {
      fetchKnowledgeItems();
    } else {
      setKnowledgeItems([]);
    }
  }, [promptId, open]);

  const handleAddKnowledge = async () => {
    if (!newKnowledge.name || !newKnowledge.content) {
      toast.error(t("promptModal.knowledge.fillNameContent"));
      return;
    }
    try {
      await api.post(`/prompt/${promptId}/knowledge`, newKnowledge);
      toast.success(t("promptModal.knowledge.addedSuccess"));
      setNewKnowledge({ name: "", type: "text", content: "" });
      setAddKnowledgeOpen(false);
      fetchKnowledgeItems();
    } catch (err) {
      toastError(err);
    }
  };

  const handleUploadFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("name", file.name);
      await api.post(`/prompt/${promptId}/knowledge/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(t("promptModal.knowledge.uploadSuccess"));
      fetchKnowledgeItems();
    } catch (err) {
      toastError(err);
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleToggleKnowledge = async (item) => {
    try {
      await api.put(`/prompt/knowledge/${item.id}`, { active: !item.active });
      fetchKnowledgeItems();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteKnowledge = async (id) => {
    if (!window.confirm(t("promptModal.knowledge.removeConfirm"))) return;
    try {
      await api.delete(`/prompt/knowledge/${id}`);
      toast.success(t("promptModal.knowledge.removeSuccess"));
      fetchKnowledgeItems();
    } catch (err) {
      toastError(err);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "url": return "🔗";
      case "pdf": return "📄";
      case "doc": return "📝";
      case "audio": return "🎵";
      case "video": return "🎬";
      default: return "📋";
    }
  };

  const handlePlayVoice = async (voice, apiKey) => {
    if (playingVoice) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingVoice(false);
      return;
    }
    if (!apiKey) {
      toast.error(t("promptModal.test.fillApiKeyVoice"));
      return;
    }
    setPlayingVoice(true);
    try {
      const { data } = await api.post("/prompt/preview-voice", {
        apiKey,
        voice: voice || "nova",
      });
      const audio = new Audio(`data:audio/mp3;base64,${data.audio}`);
      audioRef.current = audio;
      audio.onended = () => { setPlayingVoice(false); audioRef.current = null; };
      audio.play();
    } catch (err) {
      toast.error(t("promptModal.test.voiceError"));
      setPlayingVoice(false);
    }
  };

  const initialState = {
    name: "",
    prompt: "",
    voice: "texto",
    voiceKey: "",
    voiceRegion: "",
    maxTokens: 2000,
    temperature: 1,
    apiKey: "",
    queueId: null,
    maxMessages: 10,
    // New fields
    agentFunction: "geral",
    voiceTone: "amigavel",
    aiModel: "gpt-4o-mini",
    languages: ["pt-BR"],
    personality: "",
    active: true,
    greetingMessage: "",
    responseRules: "",
    allowTransfer: true,
    transferKeywords: "",
    transferMessage: "",
    responseDelay: 1,
    charLimit: 2000,
    humanizeConversation: true,
    audioEnabled: false,
    transcribeAudio: false,
    responseMode: "text",
    agentVoice: "lily",
    sendAsPTT: true,
  };

  const [prompt, setPrompt] = useState(initialState);

  useEffect(() => {
    const fetchPrompt = async () => {
      if (!promptId) {
        setPrompt(initialState);
        return;
      }
      try {
        const { data } = await api.get(`/prompt/${promptId}`);
        // Merge with defaults for new fields
        setPrompt((prev) => ({
          ...initialState,
          ...data,
          languages: data.languages || ["pt-BR"],
        }));
      } catch (err) {
        toastError(err);
      }
    };
    fetchPrompt();
  }, [promptId, open]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [testMessages, testLoading]);

  const handleClose = () => {
    setPrompt(initialState);
    setActiveTab(0);
    setTestMessages([]);
    setTestInput("");
    onClose();
  };

  const handleSavePrompt = async (values) => {
    const promptData = { ...values };
    if (values.allowTransfer !== false && !values.queueId) {
      toastError(t("promptModal.sectorRequired"));
      return;
    }
    try {
      if (promptId) {
        await api.put(`/prompt/${promptId}`, promptData);
      } else {
        await api.post("/prompt", promptData);
      }
      toast.success(t("promptModal.success"));
    } catch (err) {
      toastError(err);
    }
    handleClose();
  };

  const handleToggleLanguage = (code, values, setFieldValue) => {
    const langs = values.languages || [];
    if (langs.includes(code)) {
      setFieldValue("languages", langs.filter((l) => l !== code));
    } else {
      setFieldValue("languages", [...langs, code]);
    }
  };

  const handleSendTestMessage = async (formValues) => {
    if (!testInput.trim()) return;
    if (!formValues.apiKey) {
      toast.error(t("promptModal.test.fillApiKey"));
      return;
    }
    if (!formValues.prompt && !formValues.personality) {
      toast.error(t("promptModal.test.fillPrompt"));
      return;
    }

    const userMsg = { role: "user", content: testInput };
    setTestMessages((prev) => [...prev, userMsg]);
    setTestInput("");
    setTestLoading(true);

    try {
      const { data } = await api.post("/prompt/test", {
        apiKey: formValues.apiKey,
        prompt: formValues.prompt || formValues.personality,
        model: testModel,
        maxTokens: formValues.maxTokens || 2000,
        temperature: formValues.temperature || 1,
        message: testInput,
      });
      setTestMessages((prev) => [...prev, { role: "assistant", content: data.response }]);
    } catch (err) {
      setTestMessages((prev) => [
        ...prev,
        { role: "assistant", content: `❌ Erro: ${err?.response?.data?.error || err.message}` },
      ]);
    } finally {
      setTestLoading(false);
    }
  };

  const handleTestKeyPress = (e, formValues) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendTestMessage(formValues);
    }
  };

  return (
    <div className={classes.root}>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" scroll="paper" fullWidth>
        {/* Header */}
        <div className={classes.dialogHeader}>
          <div className={classes.headerIcon}>
            <SmartToy style={{ fontSize: 22 }} />
          </div>
          <div style={{ flex: 1 }}>
            <Typography variant="h6" style={{ fontWeight: 700, fontSize: 16 }}>
              {promptId ? t("promptModal.title.edit") : t("promptModal.title.add")}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {t("promptModal.subtitle")}
            </Typography>
          </div>
          <IconButton onClick={handleClose} size="small">
            ✕
          </IconButton>
        </div>

        <Formik
          initialValues={prompt}
          enableReinitialize={true}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSavePrompt(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ touched, errors, isSubmitting, values, setFieldValue }) => (
            <Form style={{ width: "100%" }}>
              <Tabs
                value={activeTab}
                onChange={(e, v) => setActiveTab(v)}
                className={classes.tabs}
                variant="scrollable"
                scrollButtons="auto"
                style={{ paddingLeft: 24, borderBottom: "1px solid #e0e0e0" }}
              >
                <Tab label={t("promptModal.tabs.basic")} />
                <Tab label={t("promptModal.tabs.behavior")} />
                <Tab label={t("promptModal.tabs.advanced")} />
                <Tab label={t("promptModal.tabs.knowledge")} />
                <Tab label={t("promptModal.tabs.test")} />
              </Tabs>

              {/* ===== TAB BÁSICO ===== */}
              {activeTab === 0 && (
                <div className={classes.sectionContent}>
                  <div className={classes.fieldRow}>
                    <div style={{ flex: 1 }}>
                      <Typography className={classes.label}>{t("promptModal.basic.agentName")}</Typography>
                      <Field
                        as={TextField}
                        name="name"
                        placeholder={t("promptModal.basic.agentNamePlaceholder")}
                        variant="outlined"
                        size="small"
                        fullWidth
                        error={touched.name && Boolean(errors.name)}
                        helperText={touched.name && errors.name}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <Typography className={classes.label}>{t("promptModal.basic.function")}</Typography>
                      <Select
                        value={values.agentFunction || "geral"}
                        onChange={(e) => setFieldValue("agentFunction", e.target.value)}
                        variant="outlined"
                        fullWidth
                        style={{ height: 40 }}
                      >
                        {FUNCTIONS.map((f) => (
                          <MenuItem key={f.value} value={f.value}>
                            <div>
                              <div>{t(f.labelKey)}</div>
                              <Typography variant="caption" color="textSecondary">{t(f.descKey)}</Typography>
                            </div>
                          </MenuItem>
                        ))}
                      </Select>
                    </div>
                  </div>

                  <div className={classes.fieldRow}>
                    <div style={{ flex: 1 }}>
                      <Typography className={classes.label}>{t("promptModal.basic.voiceTone")}</Typography>
                      <Select
                        value={values.voiceTone || "amigavel"}
                        onChange={(e) => setFieldValue("voiceTone", e.target.value)}
                        variant="outlined"
                        fullWidth
                        style={{ height: 40 }}
                      >
                        {VOICE_TONES.map((tone) => (
                          <MenuItem key={tone.value} value={tone.value}>{t(tone.labelKey)}</MenuItem>
                        ))}
                      </Select>
                    </div>
                    <div style={{ flex: 1 }}>
                      <Typography className={classes.label}>{t("promptModal.basic.aiModel")}</Typography>
                      <Select
                        value={values.aiModel || "gpt-4o-mini"}
                        onChange={(e) => setFieldValue("aiModel", e.target.value)}
                        variant="outlined"
                        fullWidth
                        style={{ height: 40 }}
                      >
                        {AI_MODELS.map((m) => (
                          <MenuItem key={m.value} value={m.value}>
                            <div>
                              <div>{m.label}</div>
                              <Typography variant="caption" color="textSecondary">{t(m.descKey)}</Typography>
                            </div>
                          </MenuItem>
                        ))}
                      </Select>
                    </div>
                  </div>

                  <Typography className={classes.label}>{t("promptModal.basic.apiKey")}</Typography>
                  <Field
                    as={TextField}
                    name="apiKey"
                    placeholder="sk-..."
                    type={showApiKey ? "text" : "password"}
                    variant="outlined"
                    size="small"
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowApiKey(!showApiKey)} size="small">
                            {showApiKey ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Typography className={classes.label}>{t("promptModal.basic.responseLanguages")}</Typography>
                  <div className={classes.languageChips}>
                    {LANGUAGES.map((lang) => (
                      <Chip
                        key={lang.code}
                        label={`${lang.flag} ${lang.label}`}
                        onClick={() => handleToggleLanguage(lang.code, values, setFieldValue)}
                        className={
                          (values.languages || []).includes(lang.code)
                            ? classes.chipActive
                            : classes.chipInactive
                        }
                        size="small"
                      />
                    ))}
                  </div>
                  <Typography className={classes.helperText}>
                    {t("promptModal.basic.responseLanguagesHelp")}
                  </Typography>

                  <Typography className={classes.label}>{t("promptModal.basic.personality")}</Typography>
                  <Field
                    as={TextField}
                    name="prompt"
                    placeholder={t("promptModal.basic.personalityPlaceholder")}
                    variant="outlined"
                    size="small"
                    fullWidth
                    multiline
                    rows={4}
                  />
                  <Typography className={classes.helperText}>
                    {t("promptModal.basic.personalityHelp")}
                  </Typography>

                  <div className={classes.switchRow}>
                    <Typography style={{ fontWeight: 600 }}>{t("promptModal.basic.activeAgent")}</Typography>
                    <Switch
                      checked={values.active !== false}
                      onChange={(e) => setFieldValue("active", e.target.checked)}
                      style={{ color: "#F97316" }}
                    />
                  </div>
                </div>
              )}

              {/* ===== TAB COMPORTAMENTO ===== */}
              {activeTab === 1 && (
                <div className={classes.sectionContent}>
                  <Typography className={classes.label}>{t("promptModal.behavior.greetingMessage")}</Typography>
                  <Field
                    as={TextField}
                    name="greetingMessage"
                    placeholder={t("promptModal.behavior.greetingPlaceholder")}
                    variant="outlined"
                    size="small"
                    fullWidth
                    multiline
                    rows={3}
                  />
                  <Typography className={classes.helperText}>
                    {t("promptModal.behavior.greetingHelp")}
                  </Typography>

                  <Typography className={classes.label}>{t("promptModal.behavior.responseRules")}</Typography>
                  <Field
                    as={TextField}
                    name="responseRules"
                    placeholder={t("promptModal.behavior.responseRulesPlaceholder")}
                    variant="outlined"
                    size="small"
                    fullWidth
                    multiline
                    rows={3}
                  />
                  <Typography className={classes.helperText}>
                    {t("promptModal.behavior.responseRulesHelp")}
                  </Typography>

                  <div className={classes.switchRow}>
                    <Typography style={{ fontWeight: 600 }}>{t("promptModal.behavior.allowTransfer")}</Typography>
                    <Switch
                      checked={values.allowTransfer !== false}
                      onChange={(e) => setFieldValue("allowTransfer", e.target.checked)}
                      style={{ color: "#F97316" }}
                    />
                  </div>

                  {values.allowTransfer !== false && (
                    <>
                      <Typography className={classes.label}>{t("promptModal.behavior.transferKeywords")}</Typography>
                      <Field
                        as={TextField}
                        name="transferKeywords"
                        placeholder={t("promptModal.behavior.transferKeywordsPlaceholder")}
                        variant="outlined"
                        size="small"
                        fullWidth
                      />
                      <Typography className={classes.helperText}>
                        {t("promptModal.behavior.transferKeywordsHelp")}
                      </Typography>

                      <Typography className={classes.label}>{t("promptModal.behavior.transferMessage")}</Typography>
                      <Field
                        as={TextField}
                        name="transferMessage"
                        placeholder={t("promptModal.behavior.transferMessagePlaceholder")}
                        variant="outlined"
                        size="small"
                        fullWidth
                        multiline
                        rows={2}
                      />

                      <div style={{ marginTop: 12 }}>
                        <QueueSelectSingle />
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* ===== TAB AVANÇADO ===== */}
              {activeTab === 2 && (
                <div className={classes.sectionContent}>
                  <div className={classes.sliderBox}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography className={classes.label}>
                        {t("promptModal.advanced.responseDelay")}: {values.responseDelay || 1}s
                      </Typography>
                      <IconButton size="small">⟳</IconButton>
                    </Box>
                    <Slider
                      value={values.responseDelay || 1}
                      onChange={(e, v) => setFieldValue("responseDelay", v)}
                      min={0}
                      max={10}
                      step={0.5}
                      valueLabelDisplay="auto"
                    />
                    <Typography className={classes.helperText}>
                      {t("promptModal.advanced.responseDelayHelp")}
                    </Typography>
                  </div>

                  <div className={classes.sliderBox}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography className={classes.label}>
                        {t("promptModal.advanced.charLimit")}: {values.charLimit || 2000}
                      </Typography>
                      <Switch
                        checked={true}
                        size="small"
                        style={{ color: "#F97316" }}
                      />
                    </Box>
                    <Slider
                      value={values.charLimit || 2000}
                      onChange={(e, v) => setFieldValue("charLimit", v)}
                      min={100}
                      max={5000}
                      step={100}
                      valueLabelDisplay="auto"
                    />
                    <Typography className={classes.helperText}>
                      {t("promptModal.advanced.charLimitHelp")}
                    </Typography>
                  </div>

                  <div className={classes.switchRow}>
                    <div>
                      <Typography style={{ fontWeight: 600 }}>{t("promptModal.advanced.humanize")}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {t("promptModal.advanced.humanizeHelp")}
                      </Typography>
                    </div>
                    <Switch
                      checked={values.humanizeConversation !== false}
                      onChange={(e) => setFieldValue("humanizeConversation", e.target.checked)}
                      style={{ color: "#F97316" }}
                    />
                  </div>

                  <div className={classes.audioSection}>
                    <div className={classes.switchRow} style={{ marginTop: 0 }}>
                      <Typography style={{ fontWeight: 600 }}>{t("promptModal.advanced.audioFeatures")}</Typography>
                      <Switch
                        checked={values.audioEnabled || false}
                        onChange={(e) => setFieldValue("audioEnabled", e.target.checked)}
                        style={{ color: "#F97316" }}
                      />
                    </div>

                    {values.audioEnabled && (
                      <>
                        <div className={classes.switchRow}>
                          <div>
                            <Typography style={{ fontWeight: 600, fontSize: 14 }}>{t("promptModal.advanced.transcribeAudio")}</Typography>
                            <Typography variant="caption" color="textSecondary">
                              {t("promptModal.advanced.transcribeAudioHelp")}
                            </Typography>
                          </div>
                          <Switch
                            checked={values.transcribeAudio || false}
                            onChange={(e) => setFieldValue("transcribeAudio", e.target.checked)}
                            size="small"
                          />
                        </div>

                        <Typography className={classes.label}>{t("promptModal.advanced.responseMode")}</Typography>
                        <Select
                          value={values.responseMode || "text"}
                          onChange={(e) => setFieldValue("responseMode", e.target.value)}
                          variant="outlined"
                          fullWidth
                          style={{ height: 40 }}
                        >
                          {RESPONSE_MODES.map((m) => (
                            <MenuItem key={m.value} value={m.value}>
                              <div>
                                <div>{t(m.labelKey)}</div>
                                <Typography variant="caption" color="textSecondary">{t(m.descKey)}</Typography>
                              </div>
                            </MenuItem>
                          ))}
                        </Select>

                        <Typography className={classes.label}>{t("promptModal.advanced.agentVoice")}</Typography>
                        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                          <Select
                            value={values.agentVoice || "nova"}
                            onChange={(e) => setFieldValue("agentVoice", e.target.value)}
                            variant="outlined"
                            fullWidth
                            style={{ height: 40 }}
                          >
                            {AGENT_VOICES.map((v) => (
                              <MenuItem key={v.value} value={v.value}>
                                <div>
                                  <div>{v.label}</div>
                                  <Typography variant="caption" color="textSecondary">{t(v.descKey)}</Typography>
                                </div>
                              </MenuItem>
                            ))}
                          </Select>
                          <IconButton
                            size="small"
                            onClick={() => handlePlayVoice(values.agentVoice, values.apiKey)}
                            disabled={playingVoice}
                            style={playingVoice ? { color: "#F97316" } : {}}
                          >
                            {playingVoice ? <CircularProgress size={20} style={{ color: "#F97316" }} /> : <PlayArrow />}
                          </IconButton>
                        </Box>

                        <div className={classes.switchRow}>
                          <div>
                            <Typography style={{ fontWeight: 600, fontSize: 14 }}>{t("promptModal.advanced.sendAsPTT")}</Typography>
                            <Typography variant="caption" color="textSecondary">
                              {t("promptModal.advanced.sendAsPTTHelp")}
                            </Typography>
                          </div>
                          <Switch
                            checked={values.sendAsPTT !== false}
                            onChange={(e) => setFieldValue("sendAsPTT", e.target.checked)}
                            style={{ color: "#F97316" }}
                          />
                        </div>
                      </>
                    )}
                  </div>

                  <div style={{ marginTop: 16 }}>
                    <Typography className={classes.label}>{t("promptModal.form.temperature")}</Typography>
                    <Field
                      as={TextField}
                      name="temperature"
                      variant="outlined"
                      size="small"
                      fullWidth
                      type="number"
                      inputProps={{ step: "0.1", min: "0", max: "2" }}
                      helperText={t("promptModal.advanced.temperatureHelp")}
                    />
                  </div>

                  <div className={classes.fieldRow} style={{ marginTop: 8 }}>
                    <div style={{ flex: 1 }}>
                      <Typography className={classes.label}>{t("promptModal.advanced.maxTokens")}</Typography>
                      <Field
                        as={TextField}
                        name="maxTokens"
                        variant="outlined"
                        size="small"
                        fullWidth
                        type="number"
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <Typography className={classes.label}>{t("promptModal.advanced.maxMessages")}</Typography>
                      <Field
                        as={TextField}
                        name="maxMessages"
                        variant="outlined"
                        size="small"
                        fullWidth
                        type="number"
                      />
                    </div>
                  </div>

                  {/* Campos Azure removidos - agora usa OpenAI TTS exclusivamente */}
                </div>
              )}

              {/* ===== TAB BASE DE CONHECIMENTO ===== */}
              {activeTab === 3 && (
                <div className={classes.sectionContent}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                    <div>
                      <Typography style={{ fontWeight: 700, fontSize: 15 }}>{t("promptModal.knowledge.title")}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {t("promptModal.knowledge.subtitle")}
                      </Typography>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        accept=".pdf,.doc,.docx,.txt,.mp3,.wav,.mp4,.avi,.mov"
                        onChange={handleUploadFile}
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={uploadingFile ? <CircularProgress size={16} /> : <CloudUpload />}
                        onClick={() => fileInputRef.current?.click()}
                        disabled={!promptId || uploadingFile}
                        style={{ textTransform: "none", fontSize: 12 }}
                      >
                        {t("promptModal.knowledge.upload")}
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => setAddKnowledgeOpen(true)}
                        disabled={!promptId}
                        style={{ textTransform: "none", fontSize: 12, backgroundColor: "#F97316", color: "#fff" }}
                      >
                        {t("promptModal.knowledge.add")}
                      </Button>
                    </div>
                  </div>

                  {!promptId && (
                    <Paper variant="outlined" style={{ padding: 32, textAlign: "center" }}>
                      <Typography color="textSecondary">
                        {t("promptModal.knowledge.saveFirst")}
                      </Typography>
                    </Paper>
                  )}

                  {/* Formulário de adição inline */}
                  {addKnowledgeOpen && (
                    <Paper variant="outlined" style={{ padding: 16, marginBottom: 16 }}>
                      <Typography style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>{t("promptModal.knowledge.newItem")}</Typography>
                      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                        <TextField
                          label={t("promptModal.knowledge.nameLabel")}
                          variant="outlined"
                          size="small"
                          value={newKnowledge.name}
                          onChange={(e) => setNewKnowledge({ ...newKnowledge, name: e.target.value })}
                          style={{ flex: 1 }}
                        />
                        <Select
                          value={newKnowledge.type}
                          onChange={(e) => setNewKnowledge({ ...newKnowledge, type: e.target.value })}
                          variant="outlined"
                          style={{ minWidth: 120, height: 40 }}
                        >
                          <MenuItem value="text">{t("promptModal.knowledge.textType")}</MenuItem>
                          <MenuItem value="url">{t("promptModal.knowledge.urlType")}</MenuItem>
                        </Select>
                      </div>
                      <TextField
                        label={newKnowledge.type === "url" ? t("promptModal.knowledge.urlLabel") : t("promptModal.knowledge.textLabel")}
                        variant="outlined"
                        size="small"
                        fullWidth
                        multiline
                        rows={newKnowledge.type === "url" ? 1 : 6}
                        value={newKnowledge.content}
                        onChange={(e) => setNewKnowledge({ ...newKnowledge, content: e.target.value })}
                        placeholder={newKnowledge.type === "url" ? t("promptModal.knowledge.urlPlaceholder") : t("promptModal.knowledge.textPlaceholder")}
                        style={{ marginBottom: 12 }}
                      />
                      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                        <Button size="small" onClick={() => { setAddKnowledgeOpen(false); setNewKnowledge({ name: "", type: "text", content: "" }); }}>
                          {t("promptModal.knowledge.cancelBtn")}
                        </Button>
                        <Button size="small" variant="contained" onClick={handleAddKnowledge}
                          style={{ backgroundColor: "#F97316", color: "#fff" }}
                        >
                          {t("promptModal.knowledge.saveBtn")}
                        </Button>
                      </div>
                    </Paper>
                  )}

                  {/* Lista de itens */}
                  {knowledgeLoading ? (
                    <div style={{ textAlign: "center", padding: 32 }}><CircularProgress size={32} /></div>
                  ) : knowledgeItems.length === 0 && promptId ? (
                    <Paper variant="outlined" style={{ padding: 32, textAlign: "center" }}>
                      <Typography variant="h6" style={{ fontSize: 40, marginBottom: 8 }}>📚</Typography>
                      <Typography color="textSecondary">
                        {t("promptModal.knowledge.empty")}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {t("promptModal.knowledge.emptyHelp")}
                      </Typography>
                    </Paper>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {knowledgeItems.map((item) => (
                        <Paper key={item.id} variant="outlined" style={{
                          padding: "10px 14px",
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          opacity: item.active ? 1 : 0.5
                        }}>
                          <span style={{ fontSize: 22 }}>{getTypeIcon(item.type)}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <Typography style={{ fontWeight: 600, fontSize: 13 }} noWrap>{item.name}</Typography>
                            <Typography variant="caption" color="textSecondary" noWrap>
                              {item.type.toUpperCase()} • {item.content ? `${item.content.substring(0, 60)}...` : item.originalName || t("promptModal.knowledge.noContent")}
                            </Typography>
                          </div>
                          <Switch
                            checked={item.active}
                            onChange={() => handleToggleKnowledge(item)}
                            size="small"
                            color="primary"
                          />
                          <IconButton size="small" onClick={() => handleDeleteKnowledge(item.id)} style={{ color: "#ef4444" }}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Paper>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ===== TAB TESTAR ===== */}
              {activeTab === 4 && (
                <div style={{ padding: 0 }}>
                  <div className={classes.testChatContainer} style={{ margin: 16, height: 420 }}>
                    <div className={classes.modelSelect}>
                      <FormControl variant="outlined" size="small" style={{ minWidth: 180 }}>
                        <InputLabel>{t("promptModal.test.model")}</InputLabel>
                        <Select
                          value={testModel}
                          onChange={(e) => setTestModel(e.target.value)}
                          label={t("promptModal.test.model")}
                        >
                          {AI_MODELS.map((m) => (
                            <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Typography variant="caption" color="textSecondary">
                        {t("promptModal.test.usingApiKey")}
                      </Typography>
                      <Button size="small" onClick={() => setTestMessages([])} style={{ marginLeft: "auto" }}>
                        {t("promptModal.test.clear")}
                      </Button>
                    </div>

                    <div className={classes.messagesArea}>
                      {testMessages.length === 0 && (
                        <Typography variant="body2" color="textSecondary" align="center" style={{ marginTop: 100 }}>
                          {t("promptModal.test.emptyChat")}
                        </Typography>
                      )}
                      {testMessages.map((msg, idx) => (
                        <div key={idx} style={{ display: "flex" }}>
                          <Paper
                            className={`${classes.messageBubble} ${
                              msg.role === "user" ? classes.userMessage : classes.aiMessage
                            }`}
                            elevation={0}
                          >
                            {msg.content}
                          </Paper>
                        </div>
                      ))}
                      {testLoading && (
                        <div style={{ display: "flex" }}>
                          <Paper className={`${classes.messageBubble} ${classes.aiMessage}`} elevation={0}>
                            <div className={classes.typingIndicator}>
                              <span /><span /><span />
                            </div>
                          </Paper>
                        </div>
                      )}
                      <div ref={messagesEndRef} />
                    </div>

                    <div className={classes.inputArea}>
                      <TextField
                        variant="outlined"
                        size="small"
                        fullWidth
                        placeholder={t("promptModal.test.placeholder")}
                        value={testInput}
                        onChange={(e) => setTestInput(e.target.value)}
                        onKeyPress={(e) => handleTestKeyPress(e, values)}
                        disabled={testLoading}
                      />
                      <IconButton
                        onClick={() => handleSendTestMessage(values)}
                        disabled={testLoading || !testInput.trim()}
                        style={{ color: "#F97316" }}
                      >
                        {testLoading ? <CircularProgress size={24} /> : <SendIcon />}
                      </IconButton>
                    </div>
                  </div>
                </div>
              )}

              <DialogActions className={classes.actions}>
                <Button onClick={handleClose} className={classes.cancelBtn} variant="outlined">
                  {t("promptModal.buttons.cancel")}
                </Button>
                {activeTab !== 4 && (
                  <Button
                    type="submit"
                    className={classes.saveBtn}
                    disabled={isSubmitting}
                  >
                    {t("promptModal.buttons.okEdit")}
                    {isSubmitting && <CircularProgress size={24} className={classes.buttonProgress} />}
                  </Button>
                )}
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default PromptModal;
